import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { Flex, Button } from "@chakra-ui/react";

import { InputFormField } from "./InputFormField";
import { TextareaFormField } from "./TextareaFormField";

const REQUIRED_FIELD_TEXT = "Bitte Pflichtfeld befüllen";

export type FormData = {
  text: string;
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  role: string;
  mentorId: string;
};

interface IFormContainer {
  buttonText: string;
  onSendBookingRequest: (formValues: FormData) => Promise<void>;
  onCloseModal: () => void;
  setError: () => void;
  initialFormValue: Pick<FormData, "mentorId">;
}

export const FormContainer: React.FC<IFormContainer> = ({
  initialFormValue,
  buttonText,
  onSendBookingRequest,
  onCloseModal,
  setError,
}) => {
  const [initialFormData, setInitialFormData] = useState<FormData>({
    company: "",
    role: "",
    firstName: "",
    lastName: "",
    email: "",
    mentorId: "",
    text: "",
  });
  useEffect(() => {
    setInitialFormData((currentState) => ({
      ...currentState,
      mentorId: initialFormValue.mentorId,
    }));
  }, []);

  const validationSchema = yup.object({
    email: yup
      .string()
      .email("Bitte geben Sie eine valide E-Mail-Adresse ein")
      .required(REQUIRED_FIELD_TEXT),
    mentorId: yup.string().required(),
    company: yup
      .string()
      .required(REQUIRED_FIELD_TEXT)
      .max(30, "Es sind maximal 30 Zeichen erlaubt")
      .min(3, "Bitte mindestens 3 Zeichen eingeben"),
    role: yup
      .string()
      .required(REQUIRED_FIELD_TEXT)
      .max(30, "Es sind maximal 30 Zeichen erlaubt")
      .min(3, "Bitte mindestens 3 Zeichen eingeben"),
    firstName: yup
      .string()
      .required(REQUIRED_FIELD_TEXT)
      .max(30, "Es sind maximal 30 Zeichen erlaubt")
      .min(3, "Bitte mindestens 3 Zeichen eingeben"),
    lastName: yup
      .string()
      .required(REQUIRED_FIELD_TEXT)
      .max(30, "Es sind maximal 30 Zeichen erlaubt")
      .min(3, "Bitte mindestens 3 Zeichen eingeben"),
    text: yup
      .string()
      .required(REQUIRED_FIELD_TEXT)
      .max(500, "Es sind maximal 500 Zeichen erlaubt")
      .min(3, "Bitte mindestens 3 Zeichen eingeben"),
  });

  const onSubmit = async (values: any, actions: any) => {
    await onSendBookingRequest(values)
      .then(() => {
        onCloseModal();
      })
      .catch((error) => {
        setError();
      });
  };

  const inputFormElements = [
    {
      name: "mentorId",
      label: "",
      hidden: true,
      required: false,
    },
    {
      name: "firstName",
      label: "Vorname",
      placeholderText: "z.B. Sabrina",
    },
    {
      name: "lastName",
      label: "Nachname",
      placeholderText: "z.B. Kaiser",
    },
    {
      name: "email",
      label: "E-Mail-Adresse",
      placeholderText: "z.B. sabrina@kaiser.de",
    },
    {
      name: "company",
      label: "Firmenname",
      placeholderText: "z.B. Kaiser GmbH",
    },
    {
      name: "role",
      label: "Ihre Rolle im Unternehmen",
      placeholderText: "z.B. Geschäftsführung",
    },
  ];

  const textareaFormElements = [
    {
      name: "text",
      label: "Ihre Nachricht an den Mentor",
      placeholderText: "z.B. Ich interessiere mich für ...",
    },
  ];

  return (
    <Formik
      initialValues={initialFormData}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {(formik) => {
        return (
          <Form>
            {inputFormElements.map((element, index) => (
              <InputFormField
                key={index}
                name={element.name}
                label={element.label}
                hidden={element.hidden}
                placeholderText={element.placeholderText}
                formik={formik}
              />
            ))}
            {textareaFormElements.map((element, index) => (
              <TextareaFormField
                key={index}
                name={element.name}
                label={element.label}
                placeholderText={element.placeholderText}
                formik={formik}
              />
            ))}

            <Flex justifyContent="flex-end">
              <Button
                isLoading={formik.isSubmitting}
                type="submit"
                disabled={!(formik.isValid && formik.dirty)}
                backgroundColor="highlightColor"
                color="white"
                _hover={{
                  backgroundColor: "gray.700",
                }}
              >
                {buttonText}
              </Button>
            </Flex>
          </Form>
        );
      }}
    </Formik>
  );
};
